import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Calendar, CalendarDays, ChevronLeft, ChevronRight, List, LogIn, CheckCircle, Clock, User, MapPin, X, UserPlus, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { checkInPlayer } from '../services/bookingService';
import { formatDate, formatTime, formatCurrency } from '../utils/formatters';
import { getBookingStatusLabel } from '../utils/bookingStatus';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function toDateInputValue(date) {
  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 10);
}

function getCalendarDays(month) {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const firstCell = new Date(month.getFullYear(), month.getMonth(), 1 - firstDay.getDay());
  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(firstCell);
    day.setDate(firstCell.getDate() + index);
    return day;
  });
}

function calendarBookingClass(status) {
  switch (String(status || '').toLowerCase().replaceAll('_', '')) {
    case 'confirmed':
    case 'pending':
      return 'bg-[#FFF4CC] text-[#9A5B00] hover:bg-[#FDE9A8]';
    case 'checkedin':
      return 'bg-[#E0F7F4] text-[#0B7F83] hover:bg-[#C6EEE9]';
    case 'completed':
      return 'bg-[#DCFCE7] text-[#166534] hover:bg-[#C8F0D4]';
    case 'cancelled':
      return 'bg-[#FEE2E2] text-[#B42318] hover:bg-[#FCD1D1]';
    default:
      return 'bg-[#E8F4FD] text-[#264D59] hover:bg-[#D5EAF8]';
  }
}

export default function AllBookings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [viewMode, setViewMode] = useState('list');
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [date, setDate] = useState(() => {
    const requestedDate = searchParams.get('date');
    if (/^\d{4}-\d{2}-\d{2}$/.test(requestedDate || '')) return requestedDate;
    const now = new Date();
    const timezoneOffset = now.getTimezoneOffset() * 60 * 1000;
    return new Date(now.getTime() - timezoneOffset).toISOString().slice(0, 10);
  });
  const [checkingIn, setCheckingIn] = useState(null);

  const loadBookings = useCallback(async () => {
    setLoading(true);
    try {
      const apiStatus = statusFilter === 'confirmed' ? 'active' : statusFilter;
      const res = await api.get('/bookings/owner', {
        params: viewMode === 'calendar'
          ? {
              fromDate: toDateInputValue(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1)),
              toDate: toDateInputValue(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0)),
              status: apiStatus,
            }
          : { ...(date ? { date } : {}), status: apiStatus },
      });
      setBookings(Array.isArray(res.data.bookings) ? res.data.bookings : []);
    } catch {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [calendarMonth, date, statusFilter, viewMode]);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (date) next.set('date', date);
      else next.delete('date');
      return next;
    }, { replace: true });
  }, [date, setSearchParams]);

  async function handleCheckIn(bookingId) {
    setCheckingIn(bookingId);
    try {
      await checkInPlayer(bookingId);
      setBookings((prev) =>
        prev.map((b) =>
          (b.id || b._id) === bookingId ? { ...b, status: 'CheckedIn', checkedInAt: new Date().toISOString() } : b
        )
      );
      toast.success('Player checked in');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Check-in failed');
    } finally {
      setCheckingIn(null);
    }
  }

  async function handleComplete(bookingId) {
    setCheckingIn(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/complete`);
      setBookings((prev) =>
        prev.map((b) =>
          (b.id || b._id) === bookingId ? { ...b, status: 'Completed' } : b
        )
      );
      toast.success('Booking completed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Complete failed');
    } finally {
      setCheckingIn(null);
    }
  }

  const filtered = bookings.filter((b) => {
    const status = String(b.status || '').toLowerCase().replaceAll('_', '');
    if (statusFilter === 'active' && status === 'cancelled') return false;
    if (statusFilter === 'confirmed' && !['confirmed', 'pending'].includes(status)) return false;
    if (!['active', 'all', 'confirmed'].includes(statusFilter) && status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const playerName = (b.playerName || `${b.player?.firstName || ''} ${b.player?.lastName || ''}`).toLowerCase();
    const venueName = (b.venueName || b.court?.venue?.name || '').toLowerCase();
    const courtName = (b.courtName || b.court?.name || '').toLowerCase();
    return playerName.includes(q) || venueName.includes(q) || courtName.includes(q);
  });

  const confirmedCount = bookings.filter((b) => {
    const s = (b.status || '').toLowerCase();
    return s === 'confirmed' || s === 'pending';
  }).length;

  const bookingsByDate = filtered.reduce((result, booking) => {
    if (!booking.startTime) return result;
    const key = toDateInputValue(new Date(booking.startTime));
    result[key] = [...(result[key] || []), booking];
    return result;
  }, {});
  const calendarDays = getCalendarDays(calendarMonth);
  const monthLabel = calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const statusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'confirmed' || s === 'pending')
      return 'bg-[#FFF8E1] text-[#B45309]';
    if (s === 'checkedin')
      return 'bg-[#E0F7F4] text-[#56B7AC]';
    if (s === 'cancelled')
      return 'bg-[#FDF1EB] text-[#D46C4E]';
    return 'bg-[#F1F5F9] text-[#6B7280]';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Bookings</h1>
          <p className="text-sm text-[#6B7280]">Check in players at the entrance</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/bookings/manual')}
            className="btn-primary whitespace-nowrap"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Manual Booking
          </button>
          {confirmedCount > 0 && (
            <div className="px-4 py-2 bg-[#FFF8E1] rounded-[16px]">
              <p className="text-sm text-[#B45309] font-medium">
                {confirmedCount} pending check-in{confirmedCount > 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#6B7280]" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-field"
            />
            <button type="button" onClick={() => setDate('')} className="btn-outline whitespace-nowrap text-xs">
              All dates
            </button>
          </div>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="input-field min-w-[170px]"
            aria-label="Booking status"
          >
            <option value="active">Active bookings</option>
            <option value="all">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="checkedin">Checked In</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" size={16} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by player, venue, or court..."
              className="input-field pl-9"
            />
          </div>
          {search && (
            <button onClick={() => setSearch('')} className="btn-outline text-xs">
              <X className="h-3.5 w-3.5 mr-1" />Clear
            </button>
          )}
          <div className="ml-auto flex rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-1">
            <button type="button" onClick={() => setViewMode('list')} className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors ${viewMode === 'list' ? 'bg-white text-[#264D59] shadow-sm' : 'text-[#667085] hover:text-[#264D59]'}`}>
              <List className="h-4 w-4" /> List
            </button>
            <button type="button" onClick={() => setViewMode('calendar')} className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-colors ${viewMode === 'calendar' ? 'bg-white text-[#264D59] shadow-sm' : 'text-[#667085] hover:text-[#264D59]'}`}>
              <CalendarDays className="h-4 w-4" /> Calendar
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#264D59] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : viewMode === 'calendar' ? (
        <section className="card overflow-hidden">
          <div className="flex items-center justify-between border-b border-[#E2E8F0] px-5 py-4">
            <button type="button" onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#264D59] hover:bg-[#F1F5F9]" aria-label="Previous month"><ChevronLeft className="h-5 w-5" /></button>
            <div className="text-center"><h2 className="font-semibold text-[#1F2937]">{monthLabel}</h2><p className="mt-1 text-[10px] text-[#667085]"><span className="text-[#9A5B00]">Confirmed</span> · <span className="text-[#0B7F83]">Checked in</span> · <span className="text-[#166534]">Completed</span> · <span className="text-[#B42318]">Cancelled</span></p></div>
            <button type="button" onClick={() => setCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#264D59] hover:bg-[#F1F5F9]" aria-label="Next month"><ChevronRight className="h-5 w-5" /></button>
          </div>
          <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-7 border-b border-[#E2E8F0] bg-[#F8FAFC]">
              {WEEKDAYS.map((weekday) => <div key={weekday} className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-[#667085]">{weekday}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {calendarDays.map((day) => {
                const dateKey = toDateInputValue(day);
                const dayBookings = bookingsByDate[dateKey] || [];
                const isCurrentMonth = day.getMonth() === calendarMonth.getMonth();
                const isToday = dateKey === toDateInputValue(new Date());
                return <div key={dateKey} className={`min-h-[132px] border-b border-r border-[#E2E8F0] p-2 ${isCurrentMonth ? 'bg-white' : 'bg-[#F8FAFC]'} ${isToday ? 'ring-1 ring-inset ring-[#56B7AC]' : ''}`}>
                  <button type="button" onClick={() => { setDate(dateKey); setViewMode('list'); }} className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${isToday ? 'bg-[#264D59] text-white' : isCurrentMonth ? 'text-[#1F2937] hover:bg-[#F1F5F9]' : 'text-[#98A2B3]'}`} aria-label={`View bookings for ${formatDate(day)}`}>{day.getDate()}</button>
                  <div className="mt-2 space-y-1">
                    {dayBookings.slice(0, 3).map((booking) => {
                      const courtName = booking.courtName || booking.court?.name || 'Court';
                      return <button key={booking.id || booking._id} type="button" onClick={() => navigate(`/bookings/${booking.id || booking._id}`)} className={`block w-full truncate rounded-md px-2 py-1 text-left text-[11px] font-medium ${calendarBookingClass(booking.status)}`} title={`${formatTime(booking.startTime)} · ${courtName} · ${booking.playerName || 'Player'} · ${getBookingStatusLabel(booking.status)}`}><span className="font-semibold">{formatTime(booking.startTime)}</span> · <span className="font-semibold">{courtName}</span> · {booking.playerName || 'Player'}</button>;
                    })}
                    {dayBookings.length > 3 && <button type="button" onClick={() => { setDate(dateKey); setViewMode('list'); }} className="px-1 text-left text-[11px] font-semibold text-[#0B7F83] hover:underline">+{dayBookings.length - 3} more</button>}
                  </div>
                </div>;
              })}
            </div>
          </div>
          </div>
        </section>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Calendar className="mx-auto h-12 w-12 text-[#94A3B8]" />
          <p className="mt-4 text-[#6B7280]">{date ? 'No bookings for this date' : 'No bookings found'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((booking) => {
            const id = booking.id || booking._id;
            const playerName = booking.playerName || `${booking.player?.firstName || ''} ${booking.player?.lastName || ''}`.trim() || 'Unknown';
            const venueName = booking.venueName || booking.court?.venue?.name || '';
            const courtName = booking.courtName || booking.court?.name || '';
            const status = (booking.status || '').toLowerCase();
            const isPendingCheckIn = status === 'confirmed' || status === 'pending';

            return (
              <div
                key={id}
                className="card p-4 hover:border-[#56B7AC]/40 transition-colors cursor-pointer"
                onClick={() => navigate(`/bookings/${id}`)}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#F1F5F9] flex items-center justify-center">
                      <Clock className="h-5 w-5 text-[#264D59]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[#1F2937] font-semibold">
                          {booking.startTime
                            ? `${date ? '' : `${formatDate(booking.startTime)} · `}${formatTime(booking.startTime)} — ${formatTime(booking.endTime)}`
                            : '--'}
                        </p>
                        <span className={`px-2.5 py-0.5 rounded-[30px] text-xs font-medium ${statusBadge(status)}`}>
                          {getBookingStatusLabel(booking.status).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[#6B7280]">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />{playerName}
                        </span>
                        {venueName && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />{venueName}
                          </span>
                        )}
                        {courtName && <span>· {courtName}</span>}
                      </div>
                      {booking.totalAmount != null && (
                        <p className="text-xs text-[#6B7280] mt-1">
                          {formatCurrency(booking.totalAmount)}
                          {booking.payment?.paymentMethod && ` · ${booking.payment.paymentMethod}`}
                          {booking.payment?.paymentMethod === 'Cash' && ' · Received by venue'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2">
                    {isPendingCheckIn ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleCheckIn(id); }}
                        disabled={checkingIn === id}
                        className="btn-primary whitespace-nowrap"
                      >
                        {checkingIn === id ? (
                          <span className="flex items-center gap-1">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Checking...
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <LogIn className="h-4 w-4" />
                            Check In
                          </span>
                        )}
                      </button>
                    ) : status === 'checkedin' ? (
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-sm text-[#56B7AC] font-medium">
                          <CheckCircle className="h-4 w-4" />
                          Checked In
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleComplete(id); }}
                          disabled={checkingIn === id}
                          className="inline-flex items-center px-4 py-2 text-xs font-semibold text-white bg-[#56B7AC] rounded-[14px] hover:brightness-90 transition-all duration-200 disabled:opacity-50"
                        >
                          {checkingIn === id ? 'Completing...' : 'Complete'}
                        </button>
                      </div>
                    ) : status === 'completed' ? (
                      <span className="flex items-center gap-1 text-sm text-[#56B7AC] font-medium">
                        <CheckCircle className="h-4 w-4" />
                        Completed
                      </span>
                    ) : (
                      <span className="text-sm text-[#6B7280]">{status.toUpperCase()}</span>
                    )}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(`/bookings/${id}`);
                      }}
                      className="btn-secondary whitespace-nowrap"
                    >
                      <Settings className="mr-1 h-4 w-4" />
                      Manage
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
